//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useEffect, useState, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Moment from "moment";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  ListGroup,
  Row,
  InputGroup,
  FormControl,
  Table,
} from "react-bootstrap";
import { CandidateSearchActions } from "./candidateSearchActions";
import { Context } from "../common/context/Context";
import AlertMessage from "../common/alertMessages/alertMessages";
import ErrorAlertMessage from "../common/alertMessages/errorAlertMessage";

import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CandidatePageActions } from "../candidatePageActions";
import { getCandidatesSearchCMSContent } from "../common/helpers/candidateCmsHelper";

const ProfileDetails = ({ coachAssigned, candidateCMSData }) => {
  const [searchResults, setSearchResults] = useState();
  const [personalInfo, setPersonalInfo] = useState();
  const [masterDataTimeZoneList, setMasterDataTimeZoneList] = useState([]);
  const [languagePreferences, setLanguagePreferences] = useState([]);
  const [isSmsSelected, setIsSmsSelected] = useState(false);
  const [isEmailSelected, setIsEmailSelected] = useState(false);
  const [contactPreferencesEmailList, setContactPreferencesEmailList] =
    useState([]);
  const [preferredEmailAddress, setPreferredEmailAddress] = useState("");
  const [cityResidence, setCityResidence] = useState("");
  const [preferredLanguage, setPreferredLanguage] = useState("");
  const [preferredTimeZone, setPreferredTimeZone] = useState("");
  const [programEndDate, setProgramEndDate] = useState();
  const [isGenericAccount, setIsGenericAccount] = useState(false);
  const context = useContext(Context);
  const location = useLocation();
  const candidateId = location?.state?.candidateId;
  const referenceCode = location?.state?.referenceCode;
  const rightTrackKeyNumber = location?.state?.candidateFName?.keyNumber;
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    showAlert: false,
  });
  const [errorAlert, setErrorAlert] = useState({
    showAlert: false,
  });
  const [candiateProfileCMSData, setCandiateProfileCMSData] = useState({});
  const [TPSBlackListStatus, setTPSBlackListStatus] = useState(false);

  const fetchCandidateProfileLabel = (key) => {
    if (candiateProfileCMSData && candiateProfileCMSData.Labels) {
      return (
        candiateProfileCMSData.Labels.find((el) => el.Key === key)?.Phrase || ""
      );
    }
    return "";
  };

  const getCandidatePersonalInformation = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    CandidateSearchActions.GetCandidatePersonalInformation(candidateId).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          setPersonalInfo(res?.data);
          setCityResidence(res?.data?.cityOfResidence);
          setPreferredTimeZone(res?.data?.timeZone);
          setPreferredLanguage(res?.data?.preferredLanguageCode);
          fnSetContactPrefOptions(
            res?.data?.emailAddress,
            res?.data?.secondaryEmailAddress
          );
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const fnSetContactPrefOptions = (sEmailAddress, sSecondaryEmail) => {
    let arrCONPREEmailList = [];
    if (sEmailAddress.length > 0) {
      arrCONPREEmailList.push({ name: sEmailAddress, value: "Primary" });
    }
    if (sSecondaryEmail.length > 0) {
      arrCONPREEmailList.push({ name: sSecondaryEmail, value: "Secondary" });
    }
    setContactPreferencesEmailList(arrCONPREEmailList);
  };

  const getCandidateProgramDetail = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    CandidateSearchActions.GetCandidateProgramDetail(
      referenceCode,
      candidateId
    ).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          fetchCandidateCountry(res?.data);
          setSearchResults(res?.data);
          setProgramEndDate(res?.data?.programEndDate);
          setIsGenericAccount(res?.data?.isGenericAccount);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log("profile api error");
      }
    );
  };

  const fetchCandidateCountry = async (candidateData) => {
    try {
      let cmsmyprofilepayload = {
        PageName: "MyProfile",
        MarketName: candidateData?.programCountryCode,
        language: candidateData?.programLanguageCode,
        Persona: "Default",
      };
      let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

      const res = await CandidateSearchActions.GetLanguagePrefrencesDropdown(
        cmsmyprofilepayload
      );
      if (res.status === 200 || res.status === 204) {
        let arrLangs = [];
        res.data &&
          res?.data?.MarketDetails[0]?.Supportedlanguage.map((ele) => {
            arrLangs.push({
              name: ele.LanguageName,
              value: ele.ISOCode,
            });
          });

        setLanguagePreferences(arrLangs);
      } else {
        refErrorMessageAlert.showAlert = true;
        setErrorAlert(refErrorMessageAlert);
        window.scrollTo(0, 0);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getCandidateContactDetails = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    CandidateSearchActions.GetCandidateContactDetails(candidateId).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          if (res?.data) {
            res?.data.map((result) => {
              if (
                result?.hasOptedforNotification === true &&
                result?.preferenceType === "SMS"
              ) {
                setIsSmsSelected(true);
              } else if (
                result?.hasOptedforNotification === true &&
                result?.preferenceType === "EMAIL"
              ) {
                setIsEmailSelected(true);
                setPreferredEmailAddress(result?.preferredEmailAddress);
              }
            });
          }
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log("profile api error");
      }
    );
  };

  const fetchTimeZone = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    await CandidateSearchActions.GetTimeZone(
      context?.userDetails?.language
    ).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          let arrTimezone = [];
          res?.data?.filter((item) => {
            if (item.key === "timezone") {
              item?.items?.map((tz) => {
                arrTimezone.push({
                  name: tz.title,
                  value: tz.id,
                });
              });
            }
          });

          setMasterDataTimeZoneList(arrTimezone);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const showModal = () => {
    let refAlert = JSON.parse(JSON.stringify(alert));
    refAlert.showAlert = false;
    setAlert(refAlert);
  };

  const saveProfileData = async () => {
    let objSettingUpdateJSON = {};
    objSettingUpdateJSON["id"] = candidateId;
    objSettingUpdateJSON["firstName"] = personalInfo?.firstName;
    objSettingUpdateJSON["lastName"] = personalInfo?.lastName;
    objSettingUpdateJSON["preferredName"] = personalInfo?.preferredName;
    objSettingUpdateJSON["emailAddress"] = personalInfo?.emailAddress;
    objSettingUpdateJSON["secondaryEmailAddress"] =
      personalInfo?.secondaryEmailAddress;
    objSettingUpdateJSON["preferredContactNo"] =
      personalInfo?.preferredContactNo;
    objSettingUpdateJSON["preferredLanguageCode"] = preferredLanguage;
    objSettingUpdateJSON["cityOfResidence"] = cityResidence;
    objSettingUpdateJSON["timeZone"] = preferredTimeZone;
    objSettingUpdateJSON["isGenericAccount"] = isGenericAccount;
    objSettingUpdateJSON["programEndDate"] = programEndDate;

    const res = await CandidateSearchActions.SaveCandidateProfile(
      candidateId,
      objSettingUpdateJSON
    );

    if (res.status === 204) {
      console.log("saved settings updates");
    }

    let arrData = [];
    arrData.push({
      CandidateId: candidateId,
      PreferenceType: "EMAIL",
      PreferenceValue: "Email Notification",
      HasOptedforNotification: isEmailSelected,
      PreferredEmailAddress: preferredEmailAddress,
    });
    arrData.push({
      CandidateId: candidateId,
      PreferenceType: "SMS",
      PreferenceValue: "SMS Notification",
      HasOptedforNotification: isSmsSelected,
      PreferredEmailAddress: "",
    });
    let refAlert = JSON.parse(JSON.stringify(alert));
    const resp = await CandidateSearchActions.SaveCandidateContactPreference(
      candidateId,
      arrData
    );

    if (resp) {
      if (resp.status === 200) {
        refAlert.showAlert = true;
        refAlert.type = "success";
        refAlert.message = fetchCandidateProfileLabel(
          "CandidateProfile.SaveSuccessMessage"
        );
      } else if (
        resp.status === 500 ||
        resp.status === 400 ||
        resp.status === 401
      ) {
        refAlert.showAlert = true;
        refAlert.type = "danger";
        refAlert.message = fetchCandidateProfileLabel(
          "CandidateProfile.SaveErrorMessage"
        );
      } else if (resp.status === 204) {
        refAlert.showAlert = true;
        refAlert.type = "danger";
        refAlert.message = resp
          ? resp
          : fetchCandidateProfileLabel(
            "CandidateProfile.SaveNoResponseMessage"
          );
      }
      setAlert(refAlert);
    }
  };

  const dateFormat = (date) => {
    let convDate = Moment(date).format("DD-MM-YYYY");
    return convDate;
  };

  const getCandidateBlackListStatus = async () => {
    try {
      const res = await CandidateSearchActions.GetTpsBlackListStatus(
        candidateId
      );
      if (res?.status === 200) {
        return setTPSBlackListStatus(res?.data);
      }
    } catch (error) {
      console.log("error fetching TPS_BlackList status", error);
    }
  };

  useEffect(() => {
    getCandidatePersonalInformation();
    getCandidateProgramDetail();
    getCandidateContactDetails();
    fetchTimeZone();
    getCandidateBlackListStatus();
  }, []);

  useEffect(() => {
    setCandiateProfileCMSData(
      candidateCMSData?.pageContent["CandidateProfile"]
    );
  }, [candidateCMSData]);

  return (
    <Card>
      <Card.Header>
        <Card.Title className="mb-0">
          {fetchCandidateProfileLabel(
            "CandidateProfile.PersonalDetailsHeading"
          )}{" "}
        </Card.Title>
      </Card.Header>
      <Card.Body className="text-left">
        <Card.Title className="mb-4">
          {fetchCandidateProfileLabel(
            "CandidateProfile.CandidateInformationSubHeading"
          )}
        </Card.Title>
        <Table size="sm" className="my-2" responsive>
          <tbody>
            <tr>
              <th>
                {fetchCandidateProfileLabel("CandidateProfile.FirstName")}
              </th>
              <td>{personalInfo?.firstName}</td>
            </tr>
            <tr>
              <th>{fetchCandidateProfileLabel("CandidateProfile.LastName")}</th>
              <td>{personalInfo?.lastName}</td>
            </tr>
            <tr>
              <th>
                {fetchCandidateProfileLabel("CandidateProfile.PreferredName")}
              </th>
              <td>
                {personalInfo?.preferredName !== ""
                  ? personalInfo?.preferredName
                  : fetchCandidateProfileLabel("CandidateProfile.NotAdded")}
              </td>
            </tr>
            <tr>
              <th>{fetchCandidateProfileLabel("CandidateProfile.Email")}</th>
              <td style={{ wordBreak: "break-all" }}>
                {personalInfo?.emailAddress}
              </td>
            </tr>
            <tr>
              <th>
                {fetchCandidateProfileLabel(
                  "CandidateProfile.AlternativeEmail"
                )}
              </th>
              <td style={{ wordBreak: "break-all" }}>
                {personalInfo?.secondaryEmailAddress !== ""
                  ? personalInfo?.secondaryEmailAddress
                  : fetchCandidateProfileLabel("CandidateProfile.NotAdded")}
              </td>
            </tr>
            <tr>
              <th>
                {fetchCandidateProfileLabel("CandidateProfile.PreferredNumber")}
              </th>
              <td>{personalInfo?.preferredContactNo}</td>
            </tr>
            <tr>
              <th>
                {fetchCandidateProfileLabel("CandidateProfile.KeyNumber")}
              </th>
              <td>{rightTrackKeyNumber}</td>
            </tr>
          </tbody>
        </Table>
      </Card.Body>

      <hr className="my-0" />

      <Card.Body>
        <Card.Title>
          {fetchCandidateProfileLabel("CandidateProfile.PlatformPersona")}
        </Card.Title>
        <br></br>
        <Card.Title className="mb-4">
          {fetchCandidateProfileLabel(
            "CandidateProfile.ProgramInformationSubHeading"
          )}
        </Card.Title>
        <Table size="sm" className="my-2" responsive>
          <tbody>
            <tr>
              <th>
                {fetchCandidateProfileLabel("CandidateProfile.ProgramAssigned")}
              </th>
              <td>{searchResults?.programName}</td>
            </tr>
            <tr>
              <th>
                {fetchCandidateProfileLabel("CandidateProfile.ProgramEndDate")}
              </th>
              {/* <td>{dateFormat(searchResults?.programEndDate)}</td> */}
              <td>
                {programEndDate && (
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      defaultValue="MM/DD/YYYY"
                      onChange={(date) => setProgramEndDate(date)}
                      disabled={!isGenericAccount}
                      value={Moment(programEndDate)}
                    />
                  </LocalizationProvider>
                )}
              </td>
            </tr>
            {Object.keys(coachAssigned).length > 0 && (
              <tr>
                <th>
                  {fetchCandidateProfileLabel("CandidateProfile.AssignedCoach")}
                </th>
                <td>
                  {coachAssigned?.firstName},{coachAssigned?.lastName}
                </td>
              </tr>
            )}
            <tr>
              <th>
                {fetchCandidateProfileLabel("CandidateProfile.PlatformPersona")}
              </th>
              <td>{searchResults?.persona}</td>
            </tr>
            <tr>
              <th>
                {fetchCandidateProfileLabel("CandidateProfile.JobLanded")}
              </th>
              <td>
                {searchResults?.jobLanded === true
                  ? fetchCandidateProfileLabel("CandidateProfile.JobLanded.Yes")
                  : fetchCandidateProfileLabel("CandidateProfile.JobLanded.No")}
              </td>
            </tr>
          </tbody>
        </Table>
      </Card.Body>
      <Card.Body>
        <Card.Title>
          {fetchCandidateProfileLabel(
            "CandidateProfile.ProgramAndPlatformHeading"
          )}
        </Card.Title>
        <br></br>
        <Form.Group className="mb-1">
          <Form.Label>
            {fetchCandidateProfileLabel("CandidateProfile.TimeZone")}
          </Form.Label>
          <Form.Select
            name="timeZone"
            value={preferredTimeZone}
            onChange={(e) => {
              setPreferredTimeZone(e.target.value);
            }}
            aria-label="Select TimeZone"
          >
            <option value="">
              {fetchCandidateProfileLabel("CandidateProfile.SelectTimeZone")}
            </option>
            {masterDataTimeZoneList?.map((item) => (
              <option key={item?.value} value={item?.value}>
                {item?.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-1">
          <Form.Label>
            {fetchCandidateProfileLabel("CandidateProfile.CityOfResidence")}
          </Form.Label>
          <Form.Control
            type="text"
            name="cityResidence"
            onChange={(e) => {
              setCityResidence(e.target.value);
            }}
            value={cityResidence}
            aria-label="City Text"
          />
        </Form.Group>
        <Form.Group className="mb-1">
          <Form.Label>
            {fetchCandidateProfileLabel("CandidateProfile.LanguagePreference")}
          </Form.Label>
          <Form.Select
            name="language"
            value={preferredLanguage}
            onChange={(e) => {
              setPreferredLanguage(e.target.value);
            }}
            aria-label="Select Language"
          >
            <option value="">
              {fetchCandidateProfileLabel("CandidateProfile.SelectLanguage")}
            </option>
            {languagePreferences?.map((item) => (
              <option
                key={item?.value}
                value={item?.value}
                selected={item?.value === preferredLanguage ? item?.value : ""}
              >
                {item?.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Card.Body>
      <Card.Body>
        <Card.Title>
          {fetchCandidateProfileLabel("CandidateProfile.ContactPreferences")}
        </Card.Title>
        <br></br>
        <InputGroup className="mb-1">
          <InputGroup.Checkbox
            aria-label="Checkbox for following text input"
            checked={isSmsSelected}
            onClick={() => {
              setIsSmsSelected((val) => !val);
            }}
          />
          <FormControl
            aria-label="Text input with checkbox"
            placeholder={fetchCandidateProfileLabel(
              "CandidateProfile.SmsNotification"
            )}
          />
        </InputGroup>
        <InputGroup className="mb-3">
          <InputGroup.Checkbox
            aria-label="Checkbox for following text input"
            checked={isEmailSelected}
            onClick={() => {
              setIsEmailSelected((val) => !val);
            }}
          />
          <FormControl
            aria-label="Text input with checkbox"
            placeholder={fetchCandidateProfileLabel(
              "CandidateProfile.EmailNotification"
            )}
          />
        </InputGroup>
        <Form.Group className="mb-1">
          <Form.Label>
            {fetchCandidateProfileLabel("CandidateProfile.Email")}
          </Form.Label>
          <Form.Label>
            {fetchCandidateProfileLabel(
              "CandidateProfile.SelectEmailForNotifications"
            )}
          </Form.Label>
          <Form.Select
            name="emailNotifications"
            value={preferredEmailAddress}
            onChange={(e) => {
              setPreferredEmailAddress(e.target.value);
            }}
            aria-label="Select Email"
          >
            <option value="">
              {fetchCandidateProfileLabel("CandidateProfile.SelectEmail")}
            </option>
            {contactPreferencesEmailList?.map((item) => (
              <option
                key={item?.value}
                value={item?.value}
                selected={item?.value === "Primary"}
              >
                {item?.name}
              </option>
            ))}
          </Form.Select>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="me-2">
              {" "}
              {fetchCandidateProfileLabel("CandidateProfile.TPSBlacklist")}
            </span>
            <Form.Check
              disabled
              type="switch"
              id="custom-switch"
              checked={TPSBlackListStatus}
              style={{
                transform: "scale(1.2)",
              }}
            />
          </div>
          {alert?.showAlert && (
            <AlertMessage
              alertMessage={alert?.message}
              alertType={alert?.type}
              showAlert={showModal}
            />
          )}
          <Button
            className="mt-4 w-100"
            variant="primary"
            onClick={saveProfileData}
          >
            {fetchCandidateProfileLabel("CandidateProfile.SaveButton")}
          </Button>
        </Form.Group>
      </Card.Body>
    </Card>
  );
};

const UpcomingMeetings = ({ candidateCMSData }) => {
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorAlert, setErrorAlert] = useState({
    showAlert: false,
  });
  const location = useLocation();
  const email = location?.state?.candidateFName?.emailAddress;
  let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
  const [UpCommingMeetingLabel, setUpCommingMeetingLabel] = useState({});

  const fetchUpCommingMeetingLabel = (key) => {
    if (UpCommingMeetingLabel && UpCommingMeetingLabel.Labels) {
      return (
        UpCommingMeetingLabel.Labels.find((el) => el.Key === key)?.Phrase || ""
      );
    }
    return "";
  };

  const getCandidateUpComingMeetings = async () => {
    let refUpcomingMeetings = JSON.parse(JSON.stringify(upcomingMeetings));
    try {
      const res = await CandidateSearchActions.GetCandidateUpComingMeetings(
        email,
        {
          Status: "Upcoming"
        }
      );

      if (res.status === 200 || res.status === 204) {
        if (res?.data.length > 0) {
          refUpcomingMeetings.data = res?.data;

          res.data.sort((a, b) => {
            let keyA = new Date(a.startTimeStamp),
              keyB = new Date(b.startTimeStamp);
            if (keyA < keyB) return 1;
            if (keyA > keyB) return -1;
            return 0;
          });
          setUpcomingMeetings(refUpcomingMeetings);
        } else {
          refUpcomingMeetings.data = [];
          refUpcomingMeetings.noUpcomingLabel = fetchUpCommingMeetingLabel(
            "CandidateProfile.NoUpcomingMeetingsLabel"
          );
          setUpcomingMeetings(refUpcomingMeetings);
        }
      } else {
        refErrorMessageAlert.showAlert = true;
        setErrorAlert(refErrorMessageAlert);
        window.scrollTo(0, 0);
      }
    } catch (err) {
      console.log("profile api error", err);
    }
  };

  function fnGetTimewithAMPM(sTime) {
    let strTime = Moment(new Date(sTime)).format("hh:mm a");
    return strTime;
  }
  useEffect(() => {
    const fetchMeetingTypes = async () => {
      let cmsmyCoachpayload = {
        PageName: "MyCoach",
        MarketName: "US",
        language: "en-US",
        Persona: "Default",
      };

      try {
        setLoading(true);
        const res = await CandidateSearchActions.GetLanguagePrefrencesDropdown(
          cmsmyCoachpayload
        );

        if (res.status === 200 || res.status === 204) {
          let resData = res.data;
          resData.Pages.filter((x) => {
            if (x.Key === "MyCoachPage") {
              const meetingTypeDurations = x.Components[
                "CalendarPage.ScheduleAMeetingWithCoach"
              ]?.LOVList?.map?.(({ Title: name, Key: value, Values }) => ({
                name,
                value,
                Values: Values.map(({ Key: childKey, Value }) => ({
                  parentKey: value,
                  name: Value,
                  value: childKey,
                })),
              }));

              setMeetingTypes(
                meetingTypeDurations?.map(({ name, value }) => ({
                  name,
                  value,
                })) || []
              );
            }
          });
        }
      } catch (error) {
        setError("Failed to load meeting types");
      } finally {
        setLoading(false);
      }
    };

    fetchMeetingTypes();
  }, []);
  const getMeetingTypeName = (title) => {
    if (!title) return "";

    // Find matching meeting type
    const meetingType = meetingTypes.find((mt) => mt.value === title);
    return meetingType ? meetingType.name : title;
  };

  useEffect(() => {
    const fetchData = async () => {
      await getCandidateUpComingMeetings();
    };
    fetchData();
  }, []);

  useEffect(() => {
    setUpCommingMeetingLabel(candidateCMSData?.pageContent["CandidateProfile"]);
  }, [candidateCMSData]);
  // Show loading state
  if (loading) {
    return (
      <Card>
        <Card.Header>
          <Card.Title className="mb-0">
            {fetchUpCommingMeetingLabel("CandidateProfile.UpcomingMeetings")}
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="text-center">Loading...</div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <Card.Title className="mb-0">
          {fetchUpCommingMeetingLabel("CandidateProfile.UpcomingMeetings")}
        </Card.Title>
      </Card.Header>
      {upcomingMeetings?.data?.length > 0 ? (
        upcomingMeetings?.data?.map((upcomingMeeting) => {
          const originalTitle = upcomingMeeting?.title || "";
          const meetingTypeName = getMeetingTypeName(originalTitle);
          let startTimeStamp = new Date(upcomingMeeting?.startTimeStamp);
          let endTimeStamp = new Date(upcomingMeeting?.endTimeStamp);

          let sStartTime = fnGetTimewithAMPM(startTimeStamp);
          let sEndTime = fnGetTimewithAMPM(endTimeStamp);

          let startdate = Moment(startTimeStamp).format("MM-DD-YYYY");
          return (
            <>
              <Card.Body>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <strong>
                      {upcomingMeeting?.meetingEventType
                        ? fetchUpCommingMeetingLabel(
                          "CandidateProfile.MeetingType"
                        )
                        : fetchUpCommingMeetingLabel(
                          "CandidateProfile.MeetingTitle"
                        )}
                    </strong>{" "}
                    {meetingTypeName}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>
                      {fetchUpCommingMeetingLabel("CandidateProfile.MeetingDate")}
                    </strong>{" "}
                    {startdate} . {sStartTime} - {sEndTime}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>
                      {fetchUpCommingMeetingLabel(
                        "CandidateProfile.AttendeeOrOrganizerName"
                      )}
                    </strong>{" "}
                    {upcomingMeeting?.senderEmailAddress}
                  </ListGroup.Item>
                </ListGroup>
              </Card.Body>
              <hr />
            </>
          );
        })
      ) : (
        <Card.Body>{upcomingMeetings?.noUpcomingLabel}</Card.Body>
      )}
    </Card>
  );
};

const ActivitySnapshot = ({ candidateCMSData }) => {
  const [errorAlert, setErrorAlert] = useState({
    showAlert: false,
  });

  const [activitySnapshot, setActivitySnapshot] = useState([]);
  let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
  const [activitySnapshotLabel, setActivitySnapshotLabel] = useState({});

  const fetchCandidateActitvitySnapshotLabel = (key) => {
    if (activitySnapshotLabel && activitySnapshotLabel.Labels) {
      return (
        activitySnapshotLabel.Labels.find((el) => el.Key === key)?.Phrase || ""
      );
    }
    return "";
  };

  // const fetchCandidateActivitySnapshotJson = async () => {
  //   await CandidateSearchActions.GetCandidateActivitySnapshotJSON().then(
  //     (res) => {
  //       if (res.status === 200 || res.status === 204) {
  //         setActivitySnapshot(JSON.parse(JSON.stringify(res?.data)));
  //       } else {
  //         refErrorMessageAlert.showAlert = true;
  //         setErrorAlert(refErrorMessageAlert);
  //         window.scrollTo(0, 0);
  //       }
  //     },
  //     (err) => {
  //       console.log(err);
  //     }
  //   );
  // };
  // const fetchCandidateActitvitySnapshotLabel = (key) => {
  //   let refJsonContent = JSON.parse(JSON.stringify(activitySnapshot));
  //   let label = "";

  //   refJsonContent?.Properties?.Labels?.map((x) => {
  //     if (x.Key === key) {
  //       label = x.Phrase;
  //     }
  //   });
  //   return label;
  // };

  const [lastLogin, setLastLogin] = useState();
  const [assesmentCount, setAssesmentCount] = useState();
  const [appliedJobsCount, setAppliedJobsCount] = useState();
  const [eventCount, setEventCount] = useState();
  const location = useLocation();
  const candidateId = location?.state?.candidateId;

  const candidateLogin = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    CandidateSearchActions.GetCandidateActivityes(candidateId).then(
      (res) => {
        if (res?.status === 200 || res.status === 204) {
          let activities = res?.data?.filter((data) => data?.type === "login");

          let loginDate = Moment(activities[0]?.createdTimeStamp).format(
            "DD-MM-YYYY"
          );
          setLastLogin(loginDate);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );

    CandidateSearchActions.GetAssessmentCount(candidateId).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          setAssesmentCount(res?.data);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
    CandidateSearchActions.GetAppiledJobsCount(candidateId).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          setAppliedJobsCount(res?.data);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
    CandidateSearchActions.GetEventCount(candidateId).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          setEventCount(res?.data);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };
  useEffect(() => {
    candidateLogin();
    // fetchCandidateActivitySnapshotJson();
  }, []);

  useEffect(() => {
    setActivitySnapshotLabel(
      candidateCMSData?.pageContent["CandidateActivitySnapshot"]
    );
  }, [candidateCMSData]);

  return (
    <Card>
      <Card.Header>
        <Card.Title className="mb-0">
          {fetchCandidateActitvitySnapshotLabel(
            "CandidateActivitySnapshot.CandidateActivitySnapshotHeading"
          )}
        </Card.Title>
      </Card.Header>
      <ListGroup variant="flush">
        <ListGroup.Item>
          {fetchCandidateActitvitySnapshotLabel(
            "CandidateActivitySnapshot.LastLogin"
          )}{" "}
          {lastLogin}
        </ListGroup.Item>
        <ListGroup.Item>
          {fetchCandidateActitvitySnapshotLabel(
            "CandidateActivitySnapshot.JobsAppliedFor"
          )}{" "}
          {appliedJobsCount}
        </ListGroup.Item>
        <ListGroup.Item>
          {fetchCandidateActitvitySnapshotLabel(
            "CandidateActivitySnapshot.EventsRegisteredFor"
          )}{" "}
          {eventCount}
        </ListGroup.Item>
        <ListGroup.Item>
          {fetchCandidateActitvitySnapshotLabel(
            "CandidateActivitySnapshot.CompletedAssessments"
          )}{" "}
          {assesmentCount}
        </ListGroup.Item>
      </ListGroup>
    </Card>
  );
};
const Activities = ({ candidateCMSData }) => {
  const location = useLocation();
  const candidateFname = location?.state?.candidateFName?.firstName
    ? location?.state?.candidateFName?.firstName + ", "
    : location?.state?.candidateFName + ", "
      ? location?.state?.candidateFName + ", "
      : location && location?.state?.firstName;
  const candidateLname = location?.state?.candidateLName?.lastName
    ? location?.state?.candidateLName?.lastName
    : location?.state?.candidateLName
      ? location?.state?.candidateLName
      : location && location?.state?.lastName;
  const candidateId = location?.state?.candidateId;
  const [activityJson, setActivityJson] = useState({});
  const [activitiesdata, setActivitiesData] = useState([]);
  const [loadMoreCount, setLoadMoreCount] = useState(1);
  const [errorAlert, setErrorAlert] = useState({
    showAlert: false,
  });
  const [activitiesLabel, setActivitiesLabel] = useState({});

  const fetchCandidateActivityLabel = (key) => {
    if (activitiesLabel && activitiesLabel.Labels) {
      return activitiesLabel.Labels.find((el) => el.Key === key)?.Phrase || "";
    }
    return "";
  };

  // const fetchCandidateActivityJson = async () => {
  //   let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

  //   await CandidateSearchActions.GetCandidateActivityJSON().then(
  //     (res) => {
  //       if (res.status === 200 || res.status === 204) {
  //         setActivityJson(JSON.parse(JSON.stringify(res?.data)));
  //       } else {
  //         refErrorMessageAlert.showAlert = true;
  //         setErrorAlert(refErrorMessageAlert);
  //         window.scrollTo(0, 0);
  //       }
  //     },
  //     (err) => {
  //       console.log(err);
  //     }
  //   );
  // };

  // const fetchCandidateActivityLabel = (key) => {
  //   let refJsonContent = JSON.parse(JSON.stringify(activityJson));
  //   let label = "";

  //   refJsonContent?.Properties?.Labels?.map((x) => {
  //     if (x.Key === key) {
  //       label = x.Phrase;
  //     }
  //   });
  //   return label;
  // };

  const getActivityText = (data) => {
    let key = "";
    switch (data.type) {
      case "login":
        key = "CandidateActivity.Loggedin";
        break;
      case "article":
        key = "CandidateActivity.ViewedArticle";
        break;
      case "video":
        key = "CandidateActivity.ViewedVideo";
        break;
      case "document":
        key = "CandidateActivity.ViewedDocument";
        break;
      case "jobsearch":
        key = "CandidateActivity.PerformedJobSearch";
        break;
      case "job":
        key = "CandidateActivity.ViewedJob";
        break;
      case "startassessment":
        key = "CandidateActivity.StartedAssessment";
        break;
      case "completedassesment":
        key = "CandidateActivity.CompletedAssessment";
        break;
      case "Tool":
        key = "CandidateActivity.AccessedTool";
        break;
      case "event":
        key = "CandidateActivity.EnrolledEvent";
        break;
      case "Docebo":
        key = "CandidateActivity.AccessedDocebo";
        break;
      case "UploadDocument":
        key = "CandidateActivity.UploadDocument";
        break;
      case "Group Event":
        key = "CandidateActivity.GroupEvent";
        break;
      case "ILT Events":
        key = "CandidateActivity.ILTEvents";
        break;
      case "Consult survey":
        key = "CandidateActivity.ConsultSurvey";
        break;
      case "Resume Specialist":
        key = "CandidateActivity.ResumeSpecialist";
        break;
      case "Helpdesk":
        key = "CandidateActivity.HelpdeskSurvey";
        break;
      case "Meeting Scheduled":
        let meetingWith = null;
        const hasScheduled = fetchCandidateActivityLabel(
          "CandidateActivity.HasScheduledMeeting"
        );
        const [meetingType, coachNameFromValue] = (data?.value || "").split(
          "||"
        );
        if (coachNameFromValue != null && coachNameFromValue.trim() !== "") {
          meetingWith = fetchCandidateActivityLabel(
            "CandidateActivity.MeetingWith"
          );
          return `${hasScheduled} ${meetingType || ""} ${meetingWith} ${coachNameFromValue || ""
            }`;
        }
        return `${hasScheduled} ${meetingType} 
        }`;
      case "Meeting Rescheduled":
        const hasReScheduled = fetchCandidateActivityLabel(
          "CandidateActivity.HasReScheduledMeeting"
        );
        const [meetingRescheduledType, coachName] = (data?.value || "").split(
          "||"
        );
        if (coachName != null && coachName.trim() !== "") {
          meetingWith = fetchCandidateActivityLabel(
            "CandidateActivity.MeetingWith"
          );
          return `${hasReScheduled} ${meetingRescheduledType || ""
            } ${meetingWith} ${coachName || ""}`;
        }
        return `${hasScheduled} ${meetingType} 
        }`;
      case "Meeting Canceled":
        const hasCanceledMeeting = fetchCandidateActivityLabel(
          "CandidateActivity.HasCanceledMeeting"
        );
        return `${hasCanceledMeeting} ${meetingRescheduledType || ""} ${data?.value
          }`;

      default:
        break;
    }
    let label = fetchCandidateActivityLabel(key);
    return ` ${label} ${data?.value || ""}`;
  };

  const loadMoreHandler = () => {
    setLoadMoreCount(loadMoreCount + 1);
  };

  const GetActivities = () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    CandidateSearchActions.GetCandidateActivityes(candidateId).then((res) => {
      if (res?.status === 200 || res.status === 204) {
        let activities = res?.data?.filter((data) => data?.value !== "null");
        activities?.sort((a, b) => {
          let keyA = new Date(a?.createdTimeStamp),
            keyB = new Date(b?.createdTimeStamp);
          if (keyA < keyB) return 1;
          if (keyA > keyB) return -1;
          return 0;
        });
        setActivitiesData(activities);
      } else {
        return [];
        refErrorMessageAlert.showAlert = true;
        setErrorAlert(refErrorMessageAlert);
        window.scrollTo(0, 0);
      }
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    GetActivities();
    //fetchCandidateActivityJson();
  }, []);

  useEffect(() => {
    setActivitiesLabel(candidateCMSData?.pageContent["CandidateActivity"]);
  }, [candidateCMSData]);

  function getActivityDateTime(activityTimeStamp) {
    var activityDate = Moment(activityTimeStamp).format("DD-MM-YYYY h:mm A");

    return Moment(activityTimeStamp).calendar(null, {
      lastDay: "[Yesterday] h:mm A",
      sameDay: "[Today] h:mm A",
      sameElse: function () {
        return "[" + activityDate + "]";
      },
    });
  }
  return (
    <Card>
      <Card.Header>
        <Card.Title className="mb-0">
          {fetchCandidateActivityLabel(
            "CandidateActivity.CandidateActivityHeading"
          )}
        </Card.Title>
      </Card.Header>
      <Card.Body className="chat-messages">
        {activitiesdata?.slice(0, loadMoreCount * 10)?.map((data) => {
          let activityTimeStamp = new Date(data?.createdTimeStamp);
          const browserLang = navigator.language || navigator.userLanguage;
          Moment.locale(browserLang, {
            relativeTime: {
              future: "in %s",
              past: "%s",
              s: "sec ago",
              m: "%d m ago",
              mm: "%d m ago",
              h: "%d h ago",
              hh: "%d h ago",
              d: "%d d ago",
              dd: "%d d ago",
              M: "a mth ago",
              MM: "%d mths ago",
              y: "y ago",
              yy: "%d y ago",
            },
          });
          let activityTimeAgo = Moment(activityTimeStamp).fromNow();
          let activityTime = getActivityDateTime(activityTimeStamp);

          return (
            <div className="">
              <div className="flex-grow-1">
                <small className="float-end text-navy">{activityTimeAgo}</small>
                <strong>
                  {candidateFname}
                  {candidateLname}
                </strong>{" "}
                {getActivityText(data)}
                <br />
                <small className="text-muted">{activityTime}</small>
                <br />
              </div>
              <hr />
            </div>
          );
        })}
        {activitiesdata && activitiesdata.length > 0 ? (
          <div className="d-grid">
            <Button
              variant="primary"
              onClick={loadMoreHandler}
              disabled={activitiesdata.length <= loadMoreCount * 10}
              style={
                activitiesdata.length <= loadMoreCount * 10
                  ? { backgroundColor: "var(--bs-gray)", borderColor: "var(--bs-gray)" }
                  : {}
              }
            >
              {fetchCandidateActivityLabel("CandidateActivity.LoadMore")}
            </Button>
          </div>
        ) : (
          ""
        )}
      </Card.Body>
    </Card>
  );
};

const LeaveANote = ({ candidateId, isCoachAvailable, candidateCMSData }) => {
  const [messageNote, setMessageNote] = useState("");
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    showAlert: false,
  });
  const [errorAlert, setErrorAlert] = useState({
    showAlert: false,
  });
  const [leaveNoteLabel, setLeaveNoteLabel] = useState({});

  const fetchLeaveNoteLabels = (key) => {
    if (leaveNoteLabel && leaveNoteLabel.Labels) {
      return leaveNoteLabel.Labels.find((el) => el.Key === key)?.Phrase || "";
    }
    return "";
  };

  const handleMessageNoteChange = (event) => {
    setMessageNote(event.target.value);
  };

  const fetchNoteMessage = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    await CandidateSearchActions.GetLeaveNoteMessage(candidateId).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          setMessageNote(res?.data?.coachMessage);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const showModal = () => {
    let refAlert = JSON.parse(JSON.stringify(alert));
    refAlert.showAlert = false;
    setAlert(refAlert);
  };

  const sendNote = async () => {
    let payload = {
      CoachMessage: messageNote,
    };
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    let refAlert = JSON.parse(JSON.stringify(alert));
    await CandidateSearchActions.UpdateLeaveNoteMessage(
      candidateId,
      payload
    ).then(
      (res) => {
        if (res.status === 200) {
          refAlert.showAlert = true;
          refAlert.type = "success";
          refAlert.message = fetchLeaveNoteLabels(
            "CandidateProfile.SendSuccessMessage"
          );
        } else if (
          res.status === 500 ||
          res.status === 400 ||
          res.status === 401
        ) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = fetchLeaveNoteLabels(
            "CandidateProfile.SendErrorMessage"
          );
        } else if (res.status === 204) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = res
            ? res
            : fetchLeaveNoteLabels("CandidateProfile.SendNoResponseMessage");
        }
        setAlert(refAlert);
      },
      (err) => {
        console.log(err);
      }
    );
  };

  useEffect(() => {
    fetchNoteMessage();
  }, []);

  useEffect(() => {
    setLeaveNoteLabel(candidateCMSData?.pageContent["CandidateProfile"]);
  }, [candidateCMSData]);

  return (
    <>
      {Object.keys(isCoachAvailable).length > 0 && (
        <Card>
          <Form.Group as={Row} className="mb-3">
            <Card.Title className="m-3">
              {fetchLeaveNoteLabels("CandidateProfile.LeaveANote")}
            </Card.Title>
            <Col sm={10}>
              <Form.Control
                className="m-3"
                as="textarea"
                name="textarea"
                placeholder={fetchLeaveNoteLabels(
                  "CandidateProfile.LeaveNotePlaceholder"
                )}
                rows="4"
                onChange={handleMessageNoteChange}
                value={messageNote}
                maxLength={300}
              />
              {alert?.showAlert && (
                <AlertMessage
                  alertMessage={alert?.message}
                  alertType={alert?.type}
                  showAlert={showModal}
                />
              )}
              <Button className="m-3" variant="primary" onClick={sendNote}>
                {fetchLeaveNoteLabels("CandidateProfile.SendNote")}
              </Button>
            </Col>
          </Form.Group>
        </Card>
      )}
    </>
  );
};

const CandidateProfile = () => {
  const context = useContext(Context);
  const location = useLocation();
  const candidateFname = location?.state?.candidateFName?.firstName
    ? location?.state?.candidateFName?.firstName
    : location?.state?.candidateFName
      ? location?.state?.candidateFName
      : location?.state?.firstName;
  const candidateLname = location?.state?.candidateLName?.lastName
    ? location?.state?.candidateLName?.lastName
    : location?.state?.candidateLName
      ? location?.state?.candidateLName
      : location?.state?.lastName;
  const candidateId = location?.state?.candidateId;

  const [candidateProfileLables, setCandidateProfileLables] = useState([]);
  const [getCoach, setGetCoach] = useState({});
  const [errorAlert, setErrorAlert] = useState({
    showAlert: false,
  });
  const [adminCandidateCMS, setAdminCandidateSearchCMS] = useState({
    title: "",
    description: "",
    tabNavigationTitle: "",
    pageContent: {},
  });

  const getAdminCandidateSearchCMSContent = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    const cmsContent = await getCandidatesSearchCMSContent();

    if (cmsContent) {
      setAdminCandidateSearchCMS(cmsContent);
    } else {
      refErrorMessageAlert.showAlert = true;
      setErrorAlert(refErrorMessageAlert);
      window.scrollTo(0, 0);
    }
  };

  // const fetchCandidateProfileJson = () => {
  //   let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

  //   CandidateSearchActions.GetCandidateProfileJSON().then(
  //     (res) => {
  //       if (res.status === 200 || res.status === 204) {
  //         setCandidateProfileLables(JSON.parse(JSON.stringify(res?.data)));
  //       } else {
  //         refErrorMessageAlert.showAlert = true;
  //         setErrorAlert(refErrorMessageAlert);
  //         window.scrollTo(0, 0);
  //       }
  //     },
  //     (err) => {
  //       console.log(err);
  //     }
  //   );
  // };
  // const fetchCandidateProfileLabel = (key) => {
  //   let refJsonContent = JSON.parse(JSON.stringify(candidateProfileLables));
  //   let label = "";

  //   refJsonContent?.Properties?.Labels?.map((x) => {
  //     if (x.Key === key) {
  //       label = x.Phrase;
  //     }
  //   });

  //   return label;
  // };

  const getCandidateCoachSections = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    CandidateSearchActions.GetCandidateCoachSections(candidateId).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          setGetCoach(res?.data);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
          setGetCoach({});
        }
      },
      (err) => {
        console.log("profile api error");
      }
    );
  };

  const showErrorModal = () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    refErrorMessageAlert.showAlert = false;
    setErrorAlert(refErrorMessageAlert);
  };

  useEffect(() => {
    //fetchCandidateProfileJson();
    getCandidateCoachSections();
    getAdminCandidateSearchCMSContent();
  }, []);

  return (
    <React.Fragment>
      <Helmet title="Profile" />
      <Container fluid className="p-0">
        <h1 className="h3 mb-3">{candidateFname + " " + candidateLname}</h1>
        {errorAlert?.showAlert && (
          <ErrorAlertMessage
            alertMessage="AllPages.ErrorAlertMessage"
            showAlert={showErrorModal}
          />
        )}
        <Row>
          <Col md="4" xl="4">
            <ProfileDetails
              coachAssigned={getCoach}
              candidateCMSData={adminCandidateCMS}
            />
            {context?.permissionsList?.includes(
              "Candidate.sendcandidateanote"
            ) && (
                <LeaveANote
                  candidateId={candidateId}
                  isCoachAvailable={getCoach}
                  candidateCMSData={adminCandidateCMS}
                />
              )}
          </Col>
          <Col md="8" xl="8">
            <Row>
              <Col md="4" xl="7">
                <UpcomingMeetings candidateCMSData={adminCandidateCMS} />
              </Col>
            </Row>
            <Row>
              <Col>
                {" "}
                <ActivitySnapshot candidateCMSData={adminCandidateCMS} />
              </Col>
            </Row>
            {context?.permissionsList?.includes(
              "Candidate.viewcandidateactivitylog"
            ) && (
                <Row>
                  <Col>
                    {" "}
                    <Activities candidateCMSData={adminCandidateCMS} />
                  </Col>
                </Row>
              )}
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default CandidateProfile;
